import { MapManager } from '../maps/mapmanager';
import { GameObjectManager } from './gameobjectmanager';
import { ProxyObject } from './proxyobject';

class _ZoneManager {
    map: any;
    objects: any;
    centerObj: any;
    pawns: any[];
    colObjects: any[];
    inScene: boolean;
    _transitioning: boolean;
    _readyCallbacks: { map: any; callback: (map: any) => void }[];

    constructor() {
      this.map = null;
      this.objects = null;
      this.centerObj = null;
      this.pawns = [];
      this.colObjects = [];
      this.inScene = false;
      this._transitioning = false;
      this._readyCallbacks = [];
    }

    prepare(mapIdx: any, startPos: any, callback: any) {
      this.map = new MapManager();
      this.objects = new GameObjectManager();
      this.pawns = [];
      this.colObjects = [];
      this.map.setMap(mapIdx, function(this: any) {
        this.map.setViewerInfo(startPos, function(this: any) {
          // TODO: Figure out why this is needed, maybe remove it...
          this.map.rootObj.updateMatrixWorld();
          if (callback) {
            callback();
          }
        }.bind(this));
      }.bind(this));
    }

    setCenterObject(object: any) {
      this.centerObj = object;
    }

    addToScene() {
      this.inScene = true;
      this.map.addToScene();
      for (let i = 0; i < this.pawns.length; ++i) {
        scene.add(this.pawns[i].rootObj);
      }
    }

    removeFromScene() {
      this.inScene = false;
      this.map.removeFromScene();
      for (let i = 0; i < this.pawns.length; ++i) {
        scene.remove(this.pawns[i].rootObj);
      }
    }

    transitionTo(mapIdx: any, startPos: any, callback: any) {
      if (this._transitioning) {
        console.warn('transitionTo called while a previous transition is still in progress');
        return;
      }
      this._transitioning = true;

      const center = this.centerObj;
      this._teardownForTransition(center);

      this.map = new MapManager();
      this.objects = new GameObjectManager();
      this.pawns = [];
      this.colObjects = [];
      this.map.setMap(mapIdx, function(this: any) {
        this.map.setViewerInfo(startPos, function(this: any) {
          this.map.rootObj.updateMatrixWorld();
          this.addToScene();
          if (center) {
            if (startPos && typeof center.setPosition === 'function') {
              center.setPosition(startPos.x, startPos.y, startPos.z);
            }
            this.addObject(center);
            this.setCenterObject(center);
          }
          this._transitioning = false;
          if (callback) {
            callback();
          }
          const readyCallbacks = this._readyCallbacks;
          this._readyCallbacks = [];
          for (const waiter of readyCallbacks) {
            if (waiter.map === this.map) {
              waiter.callback(this.map);
            }
          }
        }.bind(this));
      }.bind(this));
    }

    whenReady(callback: (map: any) => void) {
      if (!this._transitioning) {
        callback(this.map);
        return;
      }
      this._readyCallbacks.push({ map: this.map, callback });
    }

    _teardownForTransition(center: any) {
      const centerPawn = center ? center.pawn : null;
      try {
        if (this.inScene) {
          this.removeFromScene();
        }
        for (let i = 0; i < this.pawns.length; ++i) {
          if (this.pawns[i] !== centerPawn) {
            this._disposeObject3D(this.pawns[i].rootObj);
          }
        }
        if (this.map && this.map.rootObj) {
          this._disposeObject3D(this.map.rootObj);
        }
      } finally {
        if (center) {
          center.world = null;
        }
        this.map = null;
        this.objects = null;
        this.pawns = [];
        this.colObjects = [];
      }
    }

    _disposeObject3D(root: any) {
      if (!root || typeof root.traverse !== 'function') return;
      root.traverse((child: any) => {
        if (child.geometry && typeof child.geometry.dispose === 'function') {
          child.geometry.dispose();
        }
        const materials = Array.isArray(child.material)
          ? child.material
          : (child.material ? [child.material] : []);
        for (const material of materials) {
          for (const key of Object.keys(material)) {
            const value = material[key];
            if (value && value.isTexture && typeof value.dispose === 'function') {
              value.dispose();
            }
          }
          if (typeof material.dispose === 'function') {
            material.dispose();
          }
        }
      });
    }

    addObject(obj: any) {
      if (!this.map || !this.objects || (obj.world && obj.world !== this.map)) {
        console.warn('Object added to ZoneManager that is assigned to another zone');
        this._disposeObject3D(obj.pawn?.rootObj);
        return false;
      }
      this.pawns.push(obj.pawn);
      this.colObjects.push(obj.pawn.boundingBoxObj);
      if (this.inScene) {
        scene.add(obj.pawn.rootObj);
      }
      this.objects.addObject(obj);
      obj.world = this.map;
      obj.dropFromSky();
      return true;
    }

    removeObject(obj: any) {
      if (!(obj instanceof ProxyObject)) {
        const colObjIdx = this.colObjects.indexOf(obj.pawn.boundingBoxObj);
        const pawnIdx = this.pawns.indexOf(obj.pawn);
        if (colObjIdx !== -1) {
          this.colObjects.splice(colObjIdx, 1);
        }
        if (pawnIdx !== -1) {
          this.pawns.splice(pawnIdx, 1);
        }
        if (this.inScene) {
          scene.remove(obj.pawn.rootObj);
        }
      }
      this.objects.removeObject(obj);
    }

    findByServerObjectIdx(objectIdx: any) {
      return this.objects.findByServerObjectIdx(objectIdx);
    }

    getRefByServerObjectIdx(objectIdx: any, objectPos: any) {
      return this.objects.getRefByServerObjectIdx(objectIdx, objectPos);
    }

    update(delta: any) {
      if (this.objects) {
        this.objects.update(delta);
      }
      if (this.map) {
        if (this.centerObj) {
          this.map.setViewerInfo(this.centerObj.position);
          // Cull entities based on distance from center object
          this.cullDistantEntities(this.centerObj.position);
        }
        this.map.update(delta);
      }
    }

    cullDistantEntities(centerPos: any) {
      if (!this.inScene) return;

      const viewDistSq = Math.pow(300, 2); // Same as old terrain view distance

      // Check each pawn and toggle visibility based on distance
      for (let i = 0; i < this.pawns.length; i++) {
        const pawn = this.pawns[i];
        if (!pawn || !pawn.owner) continue;

        const owner = pawn.owner;
        if (owner.isVending) {
          pawn.rootObj.visible = false;
          continue;
        }
        // Don't cull the center object (player)
        if (owner === this.centerObj) continue;

        // Calculate distance from center
        const dx = owner.position.x - centerPos.x;
        const dy = owner.position.y - centerPos.y;
        const distSq = dx * dx + dy * dy;

        // Toggle visibility based on distance
        if (distSq <= viewDistSq) {
          // Within range - make visible
          if (!pawn.rootObj.visible) {
            pawn.rootObj.visible = true;
          }
        } else {
          // Out of range - hide
          if (pawn.rootObj.visible) {
            pawn.rootObj.visible = false;
          }
        }
      }
    }

    _meshToObject(mesh: any) {
      let parent = mesh;
      do {
        const pawn = parent.userData.owner;
        if (pawn) {
          return pawn.owner;
        }
        parent = parent.parent;
      } while (parent);
      return null;
    }

    _rayPickObjects(rayCaster: any, shouldIgnoreObject?: (object: any) => boolean) {
      const inters = rayCaster.intersectObjects(this.colObjects, true);
      return inters.find((hit: any) => {
        const object = this._meshToObject(hit.object);
        return !object?.isDead && !shouldIgnoreObject?.(object);
      }) || null;
    }

    objectRayPick(rayCaster: any) {
      let objPickInfo = this._rayPickObjects(rayCaster);
      if (objPickInfo) {
        return this._meshToObject(objPickInfo.object);
      }
      return null;
    }

    rayPick(rayCaster: any, shouldIgnoreObject?: (object: any) => boolean) {
      let objPickInfo = this._rayPickObjects(rayCaster, shouldIgnoreObject);
      let mapPickInfo = this.map.rayPick(rayCaster);
      if (mapPickInfo && objPickInfo) {
        if (mapPickInfo.distance < objPickInfo.distance) {
          // If the world is closer, remove the object pick
          objPickInfo = null;
        } else {
          // Otherwise, remove the world pick
          mapPickInfo = null;
        }
      }
      if (objPickInfo) {
        return {
          object: this._meshToObject(objPickInfo.object),
          point: objPickInfo.point
        };
      } else if (mapPickInfo) {
        return {
          object: null,
          point: mapPickInfo.point
        };
      } else {
        return null;
      }
    }

}
















export const GZM = new _ZoneManager();
